import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ExternalLink, MessageCircle, Phone, Search, Video, Plus, Info, X } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import MessageInput from '@/components/MessageInput';
import MessageThread from '@/components/MessageThread';
import { Button } from '@/components/ui/button';
import { messagingAPI } from '@/services/api';
import { useSocket } from '@/contexts/SocketContext';
import { CallSessionProvider, useCallSession } from './calls/CallSessionContext';
import useParticipantPresence from './useParticipantPresence';
import './messaging.css';

const makeClientMessageId = () => globalThis.crypto?.randomUUID?.() || `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`;
const dedupe = (items) => Array.from(new Map(items.map((item) => [item.client_message_id || item.message_id, item])).values());
const dedupeConversations = (items) => Array.from(new Map(items.map((item) => [item.conversation_id, item])).values());
const safePreview = (message) => message?.type === 'text' ? message.content : message ? 'Attachment' : 'No messages yet';

const Avatar = ({ name }) => <span className="chat-avatar" aria-hidden="true">{(name || '?').split(' ').filter(Boolean).slice(0, 2).map((part) => part[0]).join('')}</span>;

const ConversationRow = ({ conversation, active, onSelect }) => (
  <button type="button" onClick={() => onSelect(conversation)} aria-current={active ? 'true' : undefined} className={`conversation-row ${active ? 'is-active' : ''}`}>
    <Avatar name={conversation.other_party?.name}/>
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between gap-2"><p className="truncate font-semibold">{conversation.other_party?.name || 'Conversation'}</p>
      {conversation.last_message_at && <time className="shrink-0 text-[10px] opacity-70">{new Date(conversation.last_message_at).toLocaleDateString()}</time>}</div>
      <p className="truncate text-xs opacity-70">{conversation.campaign_title || 'Campaign conversation'}</p>
      <div className="mt-1 flex items-center gap-2"><p className="truncate text-sm opacity-80">{safePreview(conversation.last_message)}</p>{conversation.unread_count > 0 && <span aria-label={`${conversation.unread_count} unread messages`} className="chat-unread">{conversation.unread_count}</span>}</div>
    </div>
  </button>
);

const CampaignContext = ({ conversation, onClose }) => (
  <aside aria-label="Campaign context" className="chat-context h-full overflow-y-auto bg-white p-5">
    <div className="flex items-center justify-between gap-2"><h3 className="font-semibold">Campaign context</h3>{onClose && <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>}</div>
    {conversation ? <div className="mt-4 space-y-3 text-sm"><div><p className="text-xs text-gray-500">Campaign</p><p className="font-medium">{conversation.campaign_context?.name || conversation.campaign_title || 'Campaign conversation'}</p></div><div><p className="text-xs text-gray-500">Request</p><p className="capitalize">{conversation.campaign_context?.request_type || 'Legacy'} {conversation.campaign_context?.direct_version?.toUpperCase?.() || ''}</p></div><div><p className="text-xs text-gray-500">Goal</p><p className="capitalize">{conversation.campaign_context?.goal_type || 'Not specified'}{conversation.campaign_context?.goal_actionable === false ? ' · Coming later' : ''}</p></div><div><p className="text-xs text-gray-500">Commission</p><p>{conversation.campaign_context?.commission == null ? 'Not available' : `₦${Number(conversation.campaign_context.commission).toLocaleString()}`}</p></div><div><p className="text-xs text-gray-500">Participation</p><p className="capitalize">{conversation.campaign_context?.participation_state || conversation.participation_state || 'Open conversation'}</p></div><div><p className="text-xs text-gray-500">Next action</p><p>{conversation.campaign_context?.next_action || 'Continue the conversation'}</p></div>{conversation.campaign_context?.safe_path && <Button asChild variant="outline" size="sm" className="w-full"><Link to={conversation.campaign_context.safe_path}>Open campaign <ExternalLink className="ml-1 h-3.5 w-3.5" /></Link></Button>}<p className="text-xs text-gray-500">Only campaign-safe details are shown here.</p></div> : <p className="mt-4 text-sm text-gray-500">Select a conversation to see its campaign context.</p>}
  </aside>
);

/** Shared BRM/client shell; role-specific pages only supply copy and contacts. */
const MessagingWorkspaceContent = ({ role, contacts = [], initialConversation = null }) => {
  const { socket, connected, joinConversation, leaveConversation, sendMessage, sendTyping, markAsRead, sendCallEvent } = useSocket();
  const [params, setParams] = useSearchParams();
  const consumedConversationLinkRef = useRef(null);
  const requestedConversationId = params.get('conversation_id');
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const participantPresence = useParticipantPresence({ socket, connected, conversation: selected });
  const [messages, setMessages] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [conversationPage, setConversationPage] = useState(1);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [error, setError] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [messageSearch, setMessageSearch] = useState('');
  const [typingUser, setTypingUser] = useState(null);
  const contextPanelRef = useRef(null);
  useEffect(() => {
    if (!showContext) return undefined;
    const panel = contextPanelRef.current;
    const previousFocus = document.activeElement;
    const controls = () => Array.from(panel?.querySelectorAll('button, a[href], input, [tabindex="0"]') || []).filter((element) => !element.disabled);
    controls()[0]?.focus();
    const containFocus = (event) => {
      if (event.key !== 'Tab' || !panel || getComputedStyle(panel).position !== 'absolute') return;
      const items = controls();
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    };
    window.addEventListener('keydown', containFocus);
    return () => {
      window.removeEventListener('keydown', containFocus);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [showContext]);
  useEffect(() => {
    const dismiss = (event) => {
      if (event.key === 'Escape') {
        setShowContext(false);
        setShowContacts(false);
        setShowMessageSearch(false);
        setMessageSearch('');
      }
    };
    window.addEventListener('keydown', dismiss);
    return () => window.removeEventListener('keydown', dismiss);
  }, []);
  const selectedRef = useRef(selected);
  const selectConversationRef = useRef(null);
  const messageRequestRef = useRef(0);
  const initialConversationRef = useRef(initialConversation);
  selectedRef.current = selected;
  initialConversationRef.current = initialConversation;
  const userId = localStorage.getItem('user_id');
  const { callState, selectConversation: registerConversation, incomingConversation, alerts } = useCallSession();
  useEffect(() => { if (selected) registerConversation(selected); }, [selected, registerConversation]);
  useEffect(() => {
    if (incomingConversation && selectedRef.current?.conversation_id !== incomingConversation.conversation_id) selectConversationRef.current?.(incomingConversation);
  }, [incomingConversation]);


  const loadConversations = useCallback(async (page = 1, append = false) => {
    setLoading(true);
    try {
      const rows = (await messagingAPI.listConversations(page, 25)).conversations || [];
      setConversations((current) => append ? dedupeConversations([...current, ...rows]) : rows);
      setConversationPage(page);
      setHasMoreConversations(rows.length === 25);
      setError(null);
    }
    catch (cause) { setError(cause.message || 'Unable to load conversations.'); }
    finally { setLoading(false); }
  }, []);
  const markVisibleRead = useCallback(async (conversationId) => {
    try {
      if (connected) await markAsRead(conversationId);
      else await messagingAPI.markAsRead(conversationId);
      setConversations((rows) => rows.map((row) => row.conversation_id === conversationId ? { ...row, unread_count: 0 } : row));
    } catch { /* A retry on next visibility is safe and expected. */ }
  }, [connected, markAsRead]);
  const loadMessages = useCallback(async (conversationId) => {
    const requestId = ++messageRequestRef.current;
    try {
      const rows = (await messagingAPI.getMessages(conversationId)).data?.messages || [];
      if (
        requestId === messageRequestRef.current
        && selectedRef.current?.conversation_id === conversationId
      ) setMessages(rows);
    }
    catch (cause) {
      if (requestId === messageRequestRef.current) {
        setError(cause.message || 'Unable to load messages.');
      }
    }
  }, []);
  const selectConversation = useCallback(async (conversation) => {
    if (requestedConversationId && requestedConversationId !== conversation.conversation_id) {
      setParams((current) => { const next = new URLSearchParams(current); next.delete('conversation_id'); return next; }, { replace: true });
    }
    const previous = selectedRef.current;
    if (previous?.conversation_id !== conversation.conversation_id && previous && connected) leaveConversation(previous.conversation_id).catch(() => {});
    selectedRef.current = conversation;
    setSelected(conversation);
    setTypingUser(null);
    setMessageSearch('');
    setShowMessageSearch(false);
    setShowContext(false);
    setShowContacts(false);
    setMessages([]);
    if (connected) joinConversation(conversation.conversation_id).catch(() => {});
    await Promise.all([loadMessages(conversation.conversation_id), markVisibleRead(conversation.conversation_id)]);
  }, [connected, joinConversation, leaveConversation, loadMessages, markVisibleRead, requestedConversationId, setParams]);
  selectConversationRef.current = selectConversation;

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => {
    const requested = conversations.find((row) => row.conversation_id === requestedConversationId);
    if (!requestedConversationId) consumedConversationLinkRef.current = null;
    if (requested && consumedConversationLinkRef.current !== requestedConversationId) {
      consumedConversationLinkRef.current = requestedConversationId;
      if (selectedRef.current?.conversation_id !== requested.conversation_id) selectConversation(requested);
    }
  }, [conversations, requestedConversationId, selectConversation]);
  const initialConversationId = initialConversation?.conversation_id;
  useEffect(() => {
    if (initialConversationId && selectedRef.current?.conversation_id !== initialConversationId) {
      selectConversation(initialConversationRef.current);
    }
  }, [initialConversationId, selectConversation]);

  useEffect(() => {
    if (!socket) return undefined;
    const newMessage = (message) => {
      const current = selectedRef.current;
      setConversations((rows) => rows.map((row) => {
        if (row.conversation_id !== message.conversation_id) return row;
        const visible = current?.conversation_id === message.conversation_id;
        const incoming = message.sender_id !== userId;
        return { ...row, last_message: message, last_message_at: message.created_at, unread_count: visible || !incoming ? 0 : (row.unread_count || 0) + 1 };
      }));
      if (current?.conversation_id === message.conversation_id) {
        setMessages((rows) => dedupe([...rows, message]));
        if (message.sender_id !== userId) markVisibleRead(message.conversation_id);
      }
    };
    const userTyping = (payload) => {
      if (payload.conversation_id !== selectedRef.current?.conversation_id) return;
      setTypingUser(payload.is_typing ? payload.user_id : null);
    };
    socket.on('new_message', newMessage);
    socket.on('user_typing', userTyping);
    return () => {
      socket.off('new_message', newMessage);
      socket.off('user_typing', userTyping);
    };
  }, [markVisibleRead, socket, userId]);

  const optimisticSend = useCallback(async (content, type, attachment = null, retrying = null) => {
    if (!selected) return;
    const clientMessageId = retrying?.client_message_id || makeClientMessageId();
    const optimistic = retrying ? { ...retrying, delivery_state: 'sending' } : { message_id: `local:${clientMessageId}`, client_message_id: clientMessageId, conversation_id: selected.conversation_id, sender_id: userId, sender_type: role, type, content, file_name: attachment?.file_name, file_size: attachment?.file_size, file_storage_key: attachment?.file_storage_key, caption: attachment?.caption, created_at: new Date().toISOString(), delivery_state: 'sending' };
    setMessages((rows) => dedupe([...rows.filter((row) => row.client_message_id !== clientMessageId), optimistic]));
    const payload = { client_message_id: clientMessageId, type, content, ...attachment };
    try {
      const response = connected ? await sendMessage({ conversation_id: selected.conversation_id, ...payload }) : await messagingAPI.sendMessage(selected.conversation_id, payload);
      const canonical = response.message || response;
      setMessages((rows) => dedupe(rows.map((row) => row.client_message_id === clientMessageId ? { ...canonical, delivery_state: 'sent' } : row)));
      setConversations((rows) => rows.map((row) => row.conversation_id === selected.conversation_id ? { ...row, last_message: canonical, last_message_at: canonical.created_at, unread_count: 0 } : row));
      return canonical;
    } catch (cause) {
      setMessages((rows) => rows.map((row) => row.client_message_id === clientMessageId ? { ...row, delivery_state: 'failed' } : row));
      throw new Error(cause.message || 'Message failed to send.');
    }
  }, [connected, role, selected, sendMessage, userId]);
  const retryMessage = useCallback((message) => optimisticSend(message.content, message.type, {
    file_name: message.file_name,
    file_size: message.file_size,
    file_storage_key: message.file_storage_key,
    caption: message.caption,
  }, message), [optimisticSend]);
  const selectedConversationId = selected?.conversation_id;
  useEffect(() => {
    if (!selectedConversationId || !messagingAPI.listCalls) return undefined;
    let cancelled = false;
    const id = selectedConversationId;
    const refresh = () => messagingAPI.listCalls(id).then((result) => {
      if (cancelled) return;
      setCallHistory((result.calls || []).filter((item) => ['ended', 'missed', 'declined', 'cancelled', 'failed'].includes(item.status)).map((item) => ({
        message_id: `call:${item.call_id}`, type: 'call', created_at: item.started_at,
        content: `${item.initiator_id === userId ? 'Outgoing' : 'Incoming'} ${item.media_type} call · ${item.status === 'missed' ? 'Missed call' : item.status}`,
      })));
    }).catch(() => {});
    setCallHistory([]); refresh();
    const timer = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [selectedConversationId, callState.call?.status, userId]);
  const transcript = useMemo(() => [...messages, ...callHistory].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at)), [messages, callHistory]);
  const filtered = useMemo(() => conversations.filter((conversation) => (!unreadOnly || conversation.unread_count > 0) && `${conversation.other_party?.name || ''} ${conversation.campaign_title || ''}`.toLowerCase().includes(search.toLowerCase())), [conversations, search, unreadOnly]);
  const onTyping = useCallback((isTyping) => { if (selected && connected) sendTyping(selected.conversation_id, isTyping).catch(() => {}); }, [connected, selected, sendTyping]);
  const startContact = async (contact) => {
    try { const result = await messagingAPI.createConversation(role === 'brm' ? { campaign_id: contact.campaign_id, client_id: contact.client_id } : { campaign_id: contact.campaign_id, brm_id: contact.brm_id }); await loadConversations(); await selectConversation({ ...result.conversation, campaign_title: contact.campaign_title, other_party: { id: role === 'brm' ? contact.client_id : contact.brm_id, type: role === 'brm' ? 'client' : 'brm', name: role === 'brm' ? contact.client_name : contact.brm_name } }); }
    catch (cause) { setError(cause.message || 'Unable to start this conversation.'); }
  };

  const presenceLabel = participantPresence === 'online' ? 'Participant online: connected to chat service' : participantPresence === 'offline' ? 'Participant offline: no connected chat sessions' : 'Participant presence unavailable';
  const presenceDot = <span role="status" aria-label={presenceLabel} title={presenceLabel} className={`chat-presence-dot is-${participantPresence}`} />;
  return <div className={`messaging-page messaging-page-${role}`}>
    {error && <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{error}<Button variant="link" className="ml-2 h-auto p-0" onClick={() => loadConversations()}>Retry</Button></div>}
<div className="messaging-shell">
      <section aria-label="Conversations" className={`conversation-panel ${selected ? 'has-selection' : ''}`}>
        <div className="chat-list-search"><label className="relative block flex-1"><Search className="absolute left-3 top-3 h-4 w-4 text-gray-400"/><input aria-label="Search conversations" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people or campaigns" /></label><Button variant="ghost" size="icon" aria-label="New conversation" aria-expanded={showContacts} onClick={() => setShowContacts(!showContacts)}><Plus/></Button></div>
        {showContacts && <div className="chat-contacts"><h2 className="mb-2 text-sm font-semibold">Start a conversation</h2>{contacts.length ? contacts.map((contact) => <Button key={`${contact.brm_id || contact.client_id}-${contact.campaign_id}`} variant="ghost" className="w-full justify-start truncate" onClick={() => startContact(contact)}>Message {contact.brm_name || contact.client_name}</Button>) : <p className="text-sm text-gray-500">Open a campaign to start a conversation with your {role === 'brm' ? 'client' : 'BRM'}.</p>}</div>}
        <div className="chat-filters" aria-label="Conversation filters"><button type="button" onClick={() => alerts.requestAlerts().catch(() => {})} title="Allow call alerts while this app is running">{alerts.permission === 'granted' ? 'Alerts on' : 'Enable call alerts'}</button><button type="button" aria-pressed={!unreadOnly} onClick={() => setUnreadOnly(false)}>All</button><button type="button" aria-pressed={unreadOnly} onClick={() => setUnreadOnly(true)}>Unread</button></div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {loading && conversations.length === 0 ? <p className="p-4 text-sm text-gray-500">Loading conversations…</p> : filtered.length ? filtered.map((conversation) => (
            <ConversationRow key={conversation.conversation_id} conversation={conversation} active={selected?.conversation_id === conversation.conversation_id} onSelect={selectConversation}/>
          )) : <div className="p-8 text-center text-sm text-gray-500"><MessageCircle className="mx-auto mb-2 h-7 w-7"/>No conversations match.</div>}
          {hasMoreConversations && !search && <Button type="button" variant="ghost" className="w-full rounded-none" disabled={loading} onClick={() => loadConversations(conversationPage + 1, true)}>{loading ? 'Loading…' : 'Load older conversations'}</Button>}
        </div>
      </section>
      <main className={`conversation-main ${selected ? 'has-selection' : ''}`}>
        {selected ? <>
          <header className="conversation-header">
            {presenceDot}
            <Button variant="ghost" size="icon" className="chat-back" onClick={() => { setSelected(null); selectedRef.current = null; }} aria-label="Back to conversations"><ArrowLeft/></Button>
            <button type="button" className="chat-person" aria-label="View campaign context" onClick={() => setShowContext(true)}><Avatar name={selected.other_party?.name}/><span className="min-w-0 text-left"><span className="block truncate font-semibold">{selected.other_party?.name}</span><span className="block truncate text-xs text-gray-500">{typingUser ? 'typing…' : selected.campaign_title}</span></span></button>
            <div className="chat-actions"><Button size="icon" variant="ghost" aria-label="Start video call" disabled={!callState.enabled} title="Start video call" onClick={() => callState.startCall('video').catch((cause) => setError(cause.message))}><Video/></Button><Button size="icon" variant="ghost" aria-label="Start audio call" disabled={!callState.enabled} title="Start audio call" onClick={() => callState.startCall('audio').catch((cause) => setError(cause.message))}><Phone/></Button><Button size="icon" variant="ghost" aria-label="Search messages" aria-expanded={showMessageSearch} onClick={() => { setShowMessageSearch(!showMessageSearch); setMessageSearch(''); }}><Search/></Button><Button size="icon" variant="ghost" aria-label="Campaign context" aria-expanded={showContext} onClick={() => setShowContext(!showContext)}><Info/></Button></div>
          </header>
          {showMessageSearch && <div className="chat-message-search"><input autoFocus aria-label="Search in this conversation" placeholder="Search this conversation" value={messageSearch} onChange={(event) => setMessageSearch(event.target.value)}/><Button variant="ghost" size="icon" aria-label="Close message search" onClick={() => { setShowMessageSearch(false); setMessageSearch(''); }}><X/></Button></div>}
          <div className="chat-canvas"><MessageThread messages={transcript} search={messageSearch} currentUserId={userId} otherPartyName={selected.other_party?.name} onRetry={retryMessage}/></div>
          <MessageInput key={selected.conversation_id} onSendMessage={optimisticSend} onTypingChange={onTyping} conversationId={selected.conversation_id}/>
        </> : <div className="chat-welcome"><div className="chat-welcome-icon"><MessageCircle/></div><h2>Your next great conversation</h2><p>Keep campaign ideas, decisions and progress together.</p><p className="mt-2 text-sm">Choose a conversation to continue.</p></div>}
      </main>
      {showContext && <div ref={contextPanelRef} className="chat-context-panel" role="dialog" aria-label="Campaign details"><CampaignContext conversation={selected} onClose={() => setShowContext(false)}/></div>}
    </div>
  </div>;
};

export const MessagingWorkspace = (props) => {
  const session = useCallSession();
  return session ? <MessagingWorkspaceContent {...props} /> : <CallSessionProvider role={props.role}><MessagingWorkspaceContent {...props}/></CallSessionProvider>;
};

export default MessagingWorkspace;
